import { assign } from 'xstate5';

import {
  type FavoritesMachineAction,
  type FavoriteWithId,
  type FetchFavoritesOutput,
  type ToggleFavoriteOutput,
} from '../favorites-machine.types';
import {
  favoritesMachineLogger,
  sortFavoritesById,
  withoutDuplicateFavorites,
} from '../favorites-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

export function createFavoritesMachineActions<Favorite extends FavoriteWithId>() {
  type Action<ActionParams extends Record<string, unknown> | void = void> = FavoritesMachineAction<
    Favorite,
    ActionParams
  >;

  /**
   * Stores the resolved favorites in the context.
   */
  const onFetchFavoritesDone: Action<FetchFavoritesOutput<Favorite>> = assign(
    ({ context }, params) => {
      const updatedFavorites = [...context.favorites, ...params.favorites];

      return {
        favorites: sortFavoritesById(withoutDuplicateFavorites(updatedFavorites)),
        canFetchMoreFavorites: params.favorites.length === 10,
      };
    },
  );

  const onToggleFavoriteDone: Action<{ toggledFavorite: ToggleFavoriteOutput }> = assign(
    ({ context }, params) => {
      if (!params.toggledFavorite) return {};

      const { id, isFavorite } = params.toggledFavorite;

      return {
        favorites: context.favorites.map((favorite) => {
          const isTargetFavorite = favorite.id === id;

          return isTargetFavorite ? { ...favorite, favorited: isFavorite } : favorite;
        }),
      };
    },
  );

  const logFetchFavoritesError: Action<{ error: unknown }> = (_, params) => {
    favoritesMachineLogger.error(params.error);
  };

  const increasePage: Action = assign(({ context }) => ({
    page: context.page + 1,
  }));

  /**
   * Resets the context of the machine.
   *
   * Can be used to clear the state before restarting the machine.
   */
  const resetContext: Action = assign(() => ({
    favorites: [],
    page: 0,
    canFetchMoreFavorites: false,
  }));

  return {
    onFetchFavoritesDone,
    onToggleFavoriteDone,
    logFetchFavoritesError,
    increasePage,
    resetContext,
  };
}
